/**
 * THE MATCH MODEL. Built from the server's opening snapshot, then advanced by its own events.
 *
 * ⭐ **EVERY SHAPE HERE WAS READ OFF THE LIVE WIRE, NOT OUT OF THE DECOMPILED CLIENT.** Captured
 * 2026-08-14 from a real practice match on our own server:
 *
 *     SignalType LogicEvents -> [ InitializeGame, CustomisationsChanged, ChangeConnectionStatus x2 ]
 *     InitializeGame.GameRuntimeDatas   62 objects: Hero x2, Creature x55, Spell x5
 *     a card object: Id, DataId, Type, OwnerUserId, State, Hp, Mana, Attack, Armor, MoveCount, ...
 *     a stat: { Name, Previous, Current, BaseStat, Max, Modifiers }   (Revival.Core.IntStat)
 *     RuntimePlayerDatas: UserId, UserName, IsBot, IsFirstMover, IsReady, IsFinishedMulligan,
 *                         Mana, HandCount, PlayedCards
 *     RuntimeTimerData:   Turn, Round, OwnerId, State, TimeHash, EndTime, Duration
 *
 * `reskin-map/CLIENT-CONTRACT.json` exists because this same contract was re-derived by ad-hoc
 * regex four times in one session and was wrong in a different way each time. Reading it off the
 * wire is the cure.
 *
 * ⚠️ **THE CLIENT DECIDES NOTHING.** This model is a projection of what the server said. No legality
 * test, no cost check, no outcome is computed here - the engine owns all of it, which is what makes
 * a match payable and what stops a modified client mattering.
 */

/** `Revival.Core.GameCore.IntStat` - a stat with modifiers. `Current` is what a player sees. */
export interface IntStat {
  Name?: string;
  Previous?: number;
  Current?: number;
  BaseStat?: number;
  Max?: number;
}

/**
 * `Revival.Core.Data.Enums.RuntimeState`, verbatim (`Revival.Core.decompiled.cs:22170`).
 *
 * ⚠️ **`InChoose` IS THE MULLIGAN ZONE AND `InDiscard` IS THE GRAVEYARD.** A first pass here guessed
 * "InGraveyard" and rendered the opening five nowhere: the deal moved 5 cards out of `InDeck` and the
 * hand stayed empty, which reads exactly like a deal that never happened.
 */
export type CardState =
  | "InDeck" | "InHand" | "InTable" | "InDiscard"
  | "InChoose" | "InShowAll" | "InExile" | "InShow" | string;

export interface RuntimeObject {
  Id?: number;
  DataId?: string;
  Type?: "Hero" | "Creature" | "Spell" | string;
  OwnerUserId?: string;
  /**
   * This exact copy is MINTED, decided by the SERVER.
   *
   * ⭐ **SET AFTER THE PER-VIEWER MASK, WHICH IS WHY IT IS SAFE TO TRUST.** `MatchSession.MarkFoils`
   * runs on a payload `MaskHiddenZones` has already censored, so it can only ever mark a card this
   * viewer is allowed to identify. A face-down card has no `DataId` left and therefore cannot carry
   * this flag - the absence of a leak is structural rather than a rule anybody maintains.
   *
   * ⛔ **NEVER COMPUTED ON THE CLIENT.** It used to be, from the account's own collection, and that
   * could only ever gild YOUR OWN cards: the client has no way to know what an opponent holds, and
   * guessing from the card id would gild their copy of a card you own.
   */
  IsFoil?: boolean;
  State?: CardState;
  Hp?: IntStat;
  Mana?: IntStat;
  Attack?: IntStat;
  Armor?: IntStat;
  MoveCount?: IntStat;
  AppliedEffects?: unknown[];
  /**
   * ⭐ **WHAT THIS OBJECT *DOES*, as opposed to what has been done TO it.**
   *
   * The engine ships two effect lists per runtime object and they answer different questions.
   * `AppliedEffects` is what is stuck ON the object right now (granted taunt, a poison, a silence);
   * `ImposingEffects` is the effect vocabulary the card itself carries (`["heal-max-target"]`,
   * `["confuse-1"]`, `["taunt"]`).
   *
   * 🚨 **READING THE WRONG ONE ANSWERS CONFIDENTLY AND WRONGLY.** 2026-08-18: the role plinth
   * derived support from `AppliedEffects`, so every creature whose job is a one-shot battlecry -
   * which is most of them - had an empty list and fell through to the default. Measured on the
   * wire, the dealt deck is 8 attack / 4 defend / 6 support; the board labelled 8 of 8 ATTACK.
   * **Alex had suspected for months that the card attributes were not working. They were. The
   * READOUT was reading a field that could not answer the question it was being asked.**
   */
  ImposingEffects?: string[];
  /**
   * Order within its zone.
   *
   * ⚠️ **THE ENGINE SENDS THIS UNDER TWO DIFFERENT NAMES AND YOU NEED BOTH.** The
   * `ChangeCardPosition` event carries `Position`; the object payload in a snapshot carries the
   * card data's own `RelativePositionX`, which **defaults to -1** (`Revival.Core.decompiled.cs:9785`)
   * for a card that has never been placed. Reading only `Position` means a card that arrived in a
   * snapshot rather than through an event has no order at all. See `slotOf`.
   */
  Position?: number;
  /** The same fact, as it arrives on an object payload. -1 means "never placed". */
  RelativePositionX?: number;
}

export interface RuntimePlayer {
  UserId?: string;
  UserName?: string;
  DeckId?: string;
  IsBot?: boolean;
  IsFirstMover?: boolean;
  IsReady?: boolean;
  IsFinishedMulligan?: boolean;
  Mana?: IntStat;
  HandCount?: IntStat;
}

export interface RuntimeTimer {
  Turn?: number;
  Round?: number;
  OwnerId?: string;
  State?: string;
  TimeHash?: number;
  EndTime?: string;
  Duration?: number;
}

export function stat(s: IntStat | undefined): number {
  return typeof s?.Current === "number" ? s.Current : 0;
}

export class MatchModel {
  objects = new Map<number, RuntimeObject>();
  players: RuntimePlayer[] = [];
  timer: RuntimeTimer = {};

  /** Our own user id, resolved by <see cref="resolveSelf"/>. */
  selfId = "";

  /**
   * OUR ACCOUNT'S user id, set by the match screen from `Api.userData()` before the snapshot lands.
   *
   * 🔴 **THIS EXISTS BECAUSE "WE ARE THE PLAYER WHO IS NOT THE BOT" BREAKS THE MOMENT BOTH PLAYERS
   * ARE HUMAN.** That inference was correct for every match this game had ever run, and PvP makes it
   * silently wrong for BOTH clients at once: with no bot in `RuntimePlayerDatas`, `find(p => !p.IsBot)`
   * returns the FIRST entry, so each player is shown the same person's hand, plays their cards, and is
   * congratulated on their result. Nothing throws - the board renders perfectly for one of them.
   *
   * ⚠️ Deliberately supplied rather than inferred. An identity derived from a heuristic is exactly the
   * class of thing that answers confidently and wrongly, and "which of these two people am I" is not a
   * question worth guessing when the account already knows.
   */
  accountId = "";

  /**
   * True once an `InitializeGame` has arrived, and whether it was a REJOIN.
   *
   * 🚨 **`ReInitialize: false` MEANS "SEND `ReadyToPlayCmd` NOW", AND WITHOUT IT NO HAND IS EVER
   * DEALT.** Their own client gates on exactly this
   * (`StartupController.InitializeGameAsync`, `Ironside.decompiled.cs:66444-66452`):
   *
   *     if (!data.ReInitialize) await gameHub.PerformCommandAsync&lt;ReadyToPlayCmd&gt;();
   *
   * and `ReadyToPlayCmd` is what runs `SessionStart()` -> the nested `ReadyToMulliganCmd` -> the
   * opening hands. Miss it and the board builds with 60 objects, every one of them `InDeck`, the
   * timer sits at `NotStarted`, and it looks exactly like a server that never started the match.
   * Measured here 2026-08-14 before this was wired: `turn 0 · round 0 · NotStarted · waiting`.
   */
  snapshotSeen = false;
  reinitialize = false;

  /**
   * Event `$type`s we have seen and do not yet apply.
   *
   * ⭐ **KEPT AND REPORTED RATHER THAN IGNORED.** The engine emits 48 logic events. A client that
   * silently drops the ones it does not know looks correct right up to the move that needed one,
   * and then the board is quietly wrong - which is indistinguishable from a rules bug. Listing them
   * turns "what is left to build" into a fact instead of an estimate.
   */
  /**
   * How the match finished, once it has. `null` while it is still being played.
   *
   * ⚠️ The engine's own field names, including `looserId` - see the `EndGame` case.
   */
  ended: { winnerId: string; looserId: string; reason: string } | null = null;

  unhandled = new Map<string, number>();

  /**
   * One SAMPLE of each unhandled event, so its shape can be read off the wire.
   *
   * ⭐ This is how the contract gets learned here instead of guessed. The alternative - reading
   * `Revival.Core.decompiled.cs` and writing a handler from it - is what produced four different
   * wrong answers in one session and is why `reskin-map/CLIENT-CONTRACT.json` exists.
   */
  unhandledSample = new Map<string, unknown>();

  /**
   * VISUAL EVENTS the board should ANIMATE, drained by the renderer each frame.
   *
   * ⭐ **DRIVEN BY THE SERVER'S OWN EVENTS, NEVER BY GUESSING WHEN SOMETHING PROBABLY LANDED.**
   * `ObjectHitEvent` carries `{ RuntimeArg: { RuntimeId, StatName, From, To } }` - so the board knows
   * WHICH object was hit and BY HOW MUCH, from the engine that decided it.
   * `ObjectDestructionEvent { RuntimeId }` and `ObjectSpawnEvent { RuntimeId }` are the other two.
   *
   * ⚠️ **A QUEUE, NOT A FLAG.** Two creatures can be hit by one attack, and a flag would animate one
   * of them. It is DRAINED by the renderer so an event is played exactly once.
   */
  visuals: { kind: "hit" | "death" | "spawn"; id: number; amount?: number; by?: number }[] = [];

  /**
   * The last object to SPEND A MOVE, which is how the client identifies an attacker.
   *
   * 🚨 **`ObjectHitEvent` DOES NOT SAY WHO SWUNG.** It carries the TARGET and the stat change and
   * nothing else (`ObjectStatEffectArg`: RuntimeId, StatName, From, To). So the board could only
   * ever animate OUR attacks - the ones where the client itself knew the attacker - and the bot's
   * attacks produced a bare number and a dying card. Alex, 2026-08-14: *"my cards just disappear
   * when I play them? are they killed by the bot? if so again very poor UX because we do not see an
   * attack animation."*
   *
   * ⭐ **THE ENGINE MAKES THE ATTACKER IDENTIFIABLE ANYWAY:** `PerformAttackCmd` ends with
   * `Executor.SpendMove()` (`Revival.Core :17970`), so the object whose MoveCount drops in the same
   * batch as a hit IS the attacker. That is an inference, not a contract - which is why it is
   * cleared on every batch and never used for anything a RULE depends on. It only decides which
   * card to animate.
   */
  private lastMover: number | null = null;

  drainVisuals(): { kind: "hit" | "death" | "spawn"; id: number; amount?: number; by?: number }[] {
    const out = this.visuals;
    this.visuals = [];
    // ⚠️ Cleared with the batch it belongs to. A stale attacker would pair the NEXT hit with
    // whoever moved last turn, and animate a card that did nothing.
    this.lastMover = null;
    return out;
  }

  applySignal(name: string, payload: unknown): void {
    if (name !== "LogicEvents" || !Array.isArray(payload)) return;
    for (const ev of payload) this.applyEvent(ev as Record<string, unknown>);
  }

  /**
   * Emotes that arrived since the last drain: who sent it and which one.
   *
   * ⚠️ Queued rather than rendered here. The model owns STATE; the screen owns pixels, and a model
   * that reached into the DOM would be a second owner of the board.
   */
  private emotes: { senderId: string; emotionId: string }[] = [];

  drainEmotes(): { senderId: string; emotionId: string }[] {
    const out = this.emotes;
    this.emotes = [];
    return out;
  }

  private applyEvent(ev: Record<string, unknown>): void {
    const type = shortType(ev["$type"]);

    switch (type) {
      // ⭐ The other player said something. `PlayEmotion` is the engine's own event and carries
      // `SenderId` + `EmotionId` - see the original's `Berserk.Shared.GameCore.LogicEvents`.
      case "PlayEmotion": {
        const senderId = String(ev["SenderId"] ?? "");
        const emotionId = String(ev["EmotionId"] ?? "");
        if (senderId && emotionId) this.emotes.push({ senderId, emotionId });
        return;
      }

      case "InitializeGame":
        this.loadSnapshot(ev);
        return;

      case "ChangeConnectionStatus":
      case "CustomisationsChanged":
      case "CommandApprove":
      case "MulliganStart":
        return; // no board state of their own

      // ⭐ **THE ONE THAT MOVES CARDS BETWEEN ZONES, AND THEREFORE THE ONE THAT DEALS THE HAND.**
      // Shape read off the live wire 2026-08-14: `{ OldState, NewState, CardIds[] }`. Without it the
      // opening five stay `InDeck` and the board renders an empty hand while the server log happily
      // reports the deal - the exact "boards built with 30 cards, hand empty" symptom the old client
      // spent days on.
      case "ChangeCardsState": {
        const ids = ev["CardIds"];
        const next = ev["NewState"];
        if (Array.isArray(ids) && typeof next === "string") {
          for (const raw of ids) {
            const id = Number(raw);
            const o = this.objects.get(id);
            if (o) o.State = next;
          }
        }
        return;
      }

      // Where a card sits within its zone. Kept so the hand keeps a stable order rather than
      // re-shuffling every time a Map is iterated.
      case "ChangeCardPosition": {
        const id = Number(ev["Id"]);
        const o = this.objects.get(id);
        /* ⚠️ Write BOTH names. The object payload may later be spread over this object wholesale,
         * and a stale `RelativePositionX` beside a fresh `Position` is two owners of one fact. */
        if (o) { o.Position = Number(ev["Position"]); o.RelativePositionX = Number(ev["Position"]); }
        return;
      }

      // ⚠️ **DECK AND HAND COUNTS ARE DERIVED FROM THE OBJECTS, NOT FROM THESE EVENTS.** Both exist
      // on the wire, but a count that is maintained separately from the thing it counts is a second
      // owner and drifts silently. `zone()` is the one owner; these are acknowledged and dropped.
      case "ChangeDeckCount":
      case "ChangeHandCount":
        return;

      // ⚠️ Presentation only: which card is on top of the deck. No board state.
      case "ChangedNextDeckCard":
      case "MulliganFinished":
        return;

      // ── THE THREE THE BOARD ANIMATES ─────────────────────────────────────────────────────────
      case "ObjectHitEvent": {
        // Pair the blow with whoever just spent a move - see `lastMover`.
        const arg = ev["RuntimeArg"] as
          { RuntimeId?: number; StatName?: string; From?: number; To?: number } | undefined;
        if (arg && typeof arg.RuntimeId === "number") {
          const from = arg.From ?? 0;
          const to = arg.To ?? 0;
          // ⚠️ The engine reports From/To, so the DAMAGE is the difference - not a number we invent.
          this.visuals.push({
            kind: "hit",
            id: arg.RuntimeId,
            amount: Math.max(0, from - to),
            ...(this.lastMover !== null && this.lastMover !== arg.RuntimeId
              ? { by: this.lastMover }
              : {}),
          });
        }
        return;
      }

      case "ObjectDestructionEvent": {
        const id = ev["RuntimeId"];
        if (typeof id === "number") this.visuals.push({ kind: "death", id });
        return;
      }

      // ⭐ **THE MATCH IS OVER, AND THIS IS WHO WON.** Read off the wire rather than guessed:
      //
      //     {"WinnerId":"bot-…","LooserId":"w-…","Reason":"Surrender","Order":185}
      //
      // ⚠️ `LooserId` IS THE ENGINE'S OWN SPELLING. Correcting it here would silently read
      // `undefined` forever - the field is a contract, not prose.
      //
      // 🚨 **THIS WAS THE LAST UNHANDLED EVENT ON THE WIRE**, which is why a finished match could
      // only ever say "MATCH OVER": the client had the result and was throwing it away. Inferring
      // the winner from hero HP would have been wrong for exactly the endings that are not lethal -
      // a concession, an AFK timeout, a disconnect.
      case "EndGame": {
        const e = ev as { WinnerId?: string; LooserId?: string; Reason?: string };
        this.ended = {
          winnerId: String(e.WinnerId ?? ""),
          looserId: String(e.LooserId ?? ""),
          reason: String(e.Reason ?? ""),
        };
        return;
      }

      case "ObjectSpawnEvent": {
        const id = ev["RuntimeId"];
        if (typeof id === "number") this.visuals.push({ kind: "spawn", id });
        return;
      }

      default:
        break;
    }

    // ⭐ **A STAT EVENT: `{ RuntimeObjectId, Stat }`, WHERE `Stat.Name` SAYS WHICH STAT.**
    //
    // 🚨 **`ChangeObjectMoves` IS WHAT RESTORES A CREATURE'S ATTACK AT THE START OF ITS TURN, AND
    // NOT APPLYING IT MADE ATTACKING IMPOSSIBLE.** Measured 2026-08-14: a creature played on turn 1
    // still showed `MoveCount 0` on turn 3, so the board offered NO attacker and there was no way to
    // attack at all. Nothing failed - the event simply arrived and was counted as unhandled, which
    // is precisely the "quietly wrong board" this model's `unhandled` map exists to surface.
    //
    // ⭐ Handled by SHAPE rather than by listing event names, so `ChangeObjectAbilityMoves` and any
    // future stat event are covered by the same three lines. `Stat.Name` is the engine's own name
    // for the field (`MoveCount`, `AbilityMoveCount`, `Hp`, `Attack`, ...).
    {
      const objId = ev["RuntimeObjectId"];
      const st = ev["Stat"] as (IntStat & { Name?: string }) | undefined;
      if (typeof objId === "number" && st && typeof st === "object" && st.Name) {
        const o = this.objects.get(objId) as Record<string, unknown> | undefined;
        if (o) {
          // ⭐ A MOVE BEING SPENT NAMES THE ATTACKER - see `lastMover`. Recorded BEFORE the stat is
          // overwritten, because the drop is only visible against the previous value.
          if (st.Name === "MoveCount") {
            const had = (o["MoveCount"] as IntStat | undefined)?.Current;
            const now = (st as IntStat).Current;
            if (typeof had === "number" && typeof now === "number" && now < had) {
              this.lastMover = objId;
            }
          }
          o[st.Name] = st;
          return;
        }
      }
    }

    // ⭐ **ANY EVENT CARRYING A FULL RuntimeData UPDATES THE OBJECT IT DESCRIBES.** Most of the
    // engine's events do - it is how the original client stays in sync - so this handles the bulk
    // generically instead of enumerating 48 cases up front and getting several of them wrong.
    let touched = false;
    // ⚠️ **THE PAYLOAD KEY IS NOT ALWAYS `RuntimeData`.** `ChangePlayerState` carries
    // `RuntimePlayerData` (SINGULAR), which is a different name from both `RuntimeData` and the
    // snapshot's `RuntimePlayerDatas` (plural array). Missing it meant the player's Foxfire pool
    // never updated: turn 1 rendered `foxfire 0`, every card in hand showed as unaffordable, and the
    // board looked like a game that had dealt a hand nobody could play.
    for (const key of ["RuntimeData", "GameRuntimeData", "RuntimeObjectData", "RuntimePlayerData"]) {
      const d = ev[key] as Record<string, unknown> | undefined;
      if (!d || typeof d !== "object") continue;

      // ⚠️ **`RuntimeData` IS NOT ONE TYPE.** `ChangedTimer` carries the TIMER under that name,
      // `ChangePlayerState` carries a PLAYER, and the object events carry a card. Keying on the name
      // alone put a timer into the object map, where it matched nothing and counted as unhandled.
      // Routed by what the payload actually IS.
      if (typeof d["Id"] === "number") {
        const id = d["Id"] as number;
        this.objects.set(id, { ...this.objects.get(id), ...(d as RuntimeObject) });
        touched = true;
      } else if (typeof d["TimeHash"] === "number" || typeof d["OwnerId"] === "string") {
        this.timer = { ...this.timer, ...(d as RuntimeTimer) };
        touched = true;
      } else if (typeof d["UserId"] === "string") {
        const p = d as RuntimePlayer;
        const i = this.players.findIndex((x) => x.UserId === p.UserId);
        if (i >= 0) this.players[i] = { ...this.players[i], ...p };
        else this.players.push(p);
        touched = true;
      }
    }
    const timer = ev["RuntimeTimerData"] as RuntimeTimer | undefined;
    if (timer && typeof timer === "object") {
      this.timer = { ...this.timer, ...timer };
      touched = true;
    }
    const players = ev["RuntimePlayerDatas"] as RuntimePlayer[] | undefined;
    if (Array.isArray(players)) {
      this.players = players;
      touched = true;
    }

    if (!touched) {
      this.unhandled.set(type, (this.unhandled.get(type) ?? 0) + 1);
      if (!this.unhandledSample.has(type)) this.unhandledSample.set(type, ev);
    }
  }

  private loadSnapshot(ev: Record<string, unknown>): void {
    this.snapshotSeen = true;
    this.reinitialize = ev["ReInitialize"] === true;

    const objs = ev["GameRuntimeDatas"];
    if (Array.isArray(objs)) {
      this.objects.clear();
      for (const o of objs as RuntimeObject[]) {
        if (typeof o.Id === "number") this.objects.set(o.Id, o);
      }
    }
    const players = ev["RuntimePlayerDatas"];
    if (Array.isArray(players)) this.players = players as RuntimePlayer[];

    const timer = ev["RuntimeTimerData"] as RuntimeTimer | undefined;
    if (timer) this.timer = timer;

    this.resolveSelf();
  }

  /**
   * WHICH SEAT IS OURS.
   *
   * ⭐ **THE ACCOUNT ID WINS WHENEVER IT IS PRESENT IN THE MATCH.** That is the only answer that is
   * correct in both modes: in PvE it picks the human, and in PvP it picks the right human out of two.
   *
   * ⚠️ **THE `!IsBot` FALLBACK IS KEPT, AND ONLY AS A FALLBACK.** It is still right for every
   * single-player match, and it covers the window before `Api.userData()` has answered - a snapshot
   * that arrives first must not leave the board with no owner at all, which would render an empty hand.
   * But it must never be reached in a PvP match: with two humans it cannot distinguish them, so a
   * warning is logged rather than a silent wrong guess.
   */
  /**
   * Re-run the seat resolution after `accountId` arrives.
   *
   * ⚠️ Public because the account id and the snapshot race each other and either can win. If the
   * snapshot landed first, `selfId` currently holds the fallback guess and MUST be corrected the
   * moment the real identity is known - otherwise a PvP board stays drawn from the wrong side for the
   * whole match, which is a correct-looking game of someone else's cards.
   */
  reresolveSelf(): void {
    this.resolveSelf();
  }

  private resolveSelf(): void {
    if (this.accountId && this.players.some((p) => p.UserId === this.accountId)) {
      this.selfId = this.accountId;
      return;
    }
    const humans = this.players.filter((p) => !p.IsBot);
    if (humans.length > 1) {
      // Loud, because the board is about to be drawn from the wrong side and everything below this
      // point will look completely normal.
      console.warn("[match] two human seats and no account id yet - self is a GUESS until userData lands");
    }
    this.selfId = humans[0]?.UserId ?? "";
  }

  self(): RuntimePlayer | undefined {
    return this.players.find((p) => p.UserId === this.selfId);
  }

  opponent(): RuntimePlayer | undefined {
    return this.players.find((p) => p.UserId !== this.selfId);
  }

  hero(userId: string | undefined): RuntimeObject | undefined {
    for (const o of this.objects.values()) {
      if (o.Type === "Hero" && o.OwnerUserId === userId) return o;
    }
    return undefined;
  }

  /** Cards belonging to `userId` in a given zone. */
  /*
   * 🚨 **THE ROW RENDERED IN MAP-INSERTION ORDER, SO PLACEMENT WAS DISCARDED AT THE LAST STEP.**
   * DayDevil, beta feedback 2026-08-20: *"cards played seem to shift to the left. I would allow for
   * strategic placement, cards played seem to shift to the left."*
   *
   * ⭐ **THE ENGINE WAS NEVER THE PROBLEM AND THAT IS THE IMPORTANT PART.** `PlayCardCmd` takes
   * `RelativePositionX`, the client has always sent the slot the player dropped on, and the engine
   * inserts at that index and then renumbers the row contiguously
   * (`Revival.Core.decompiled.cs:17055-17066`). So the ORDER the player chose is correct and
   * authoritative on the server. This function then threw it away by returning
   * `this.objects.values()` - a Map iterated in **creation order** - and `side()` laid that straight
   * into slots 0..6. A creature inserted at the far left rendered wherever it happened to be created.
   *
   * ⛔ **SORT THE DENSE LIST; DO NOT SCATTER INTO SLOTS BY INDEX.** The engine keeps the row packed
   * with no gaps, so position N is the Nth card, not "the card in slot N". Indexing an array BY
   * position would leave holes on any off-by-one and pile every unplaced card onto slot 0.
   *
   * ⚠️ Unknown positions sort LAST and keep their existing relative order, so a zone the engine has
   * not numbered degrades to exactly today's behaviour instead of collapsing.
   */
  zone(userId: string | undefined, state: CardState): RuntimeObject[] {
    const out: RuntimeObject[] = [];
    for (const o of this.objects.values()) {
      if (o.Type !== "Hero" && o.OwnerUserId === userId && o.State === state) out.push(o);
    }
    return out
      .map((o, i) => ({ o, i, p: slotOf(o) }))
      .sort((a, b) => (a.p ?? Infinity) - (b.p ?? Infinity) || a.i - b.i)
      .map((x) => x.o);
  }

  /** True when it is our turn. Read from the timer's owner, never inferred from a count. */
  ourTurn(): boolean {
    return !!this.selfId && this.timer.OwnerId === this.selfId;
  }
}

/**
 * The card's order within its zone, or `undefined` when the engine has not placed it.
 *
 * ⛔ **-1 IS NOT A POSITION, IT IS THE ABSENCE OF ONE.** `RelativePositionX` initialises to -1
 * (`Revival.Core.decompiled.cs:9785`). Treating it as a number sorts every unplaced card in front
 * of slot 0, which is worse than not sorting at all - and it would look like a deliberate layout.
 */
export function slotOf(o: RuntimeObject): number | undefined {
  for (const v of [o.Position, o.RelativePositionX]) {
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  }
  return undefined;
}

/** `Revival.Core.GameCore.LogicEvents.NextTurn, Revival.Core` -> `NextTurn`. */
export function shortType(t: unknown): string {
  const s = String(t ?? "");
  const name = s.split(",")[0] ?? "";
  const parts = name.split(".");
  return parts[parts.length - 1] || "(unknown)";
}
